/**
 * Card component inspired by Park UI's design system
 */

import React from "react";
import { css } from "../../lib/panda";
import { presets } from "../../styles";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "elevated" | "outlined";
  children: React.ReactNode;
}

const cardVariants = {
  default: presets.card.base,
  elevated: presets.card.elevated,
  outlined: css({
    backgroundColor: "white",
    borderRadius: "0.5rem",
    border: "1px solid #e5e5e5",
    padding: "1.5rem",
  }),
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ variant = "default", children, className, style, ...props }, ref) => {
    const cardStyles = css(cardVariants[variant], style);

    return (
      <div ref={ref} style={cardStyles} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = "Card";

// Card sub-components
export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, style, ...props }, ref) => {
  const headerStyles = css(
    {
      paddingBottom: "1rem",
      borderBottom: "1px solid #e5e5e5",
      marginBottom: "1rem",
    },
    style
  );

  return (
    <div ref={ref} style={headerStyles} {...props}>
      {children}
    </div>
  );
});

CardHeader.displayName = "CardHeader";

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, style, ...props }, ref) => {
  const contentStyles = css(
    {
      padding: "0",
    },
    style
  );

  return (
    <div ref={ref} style={contentStyles} {...props}>
      {children}
    </div>
  );
});

CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, style, ...props }, ref) => {
  const footerStyles = css(
    {
      paddingTop: "1rem",
      borderTop: "1px solid #e5e5e5",
      marginTop: "1rem",
    },
    style
  );

  return (
    <div ref={ref} style={footerStyles} {...props}>
      {children}
    </div>
  );
});

CardFooter.displayName = "CardFooter";
